import { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { Grid } from "@mui/material";
import { toast } from "react-toastify";
import { LocaleContext } from "../../../routing/LangRouter.js";
import { t } from "i18next";

//import general components
import { BaseButton } from "../base-button/base-button.jsx";
import BasePopover from "../base-popover/base-popover.jsx";
import BaseTextField from "../base-text-field/base-text-field.jsx";
import AutoCompleteComponent from "../base-select/autocomplete-component.js.jsx";

//import images and icons
import { Add, Trash } from "iconsax-react";

//import styles
import "./rooms-count.scss";

function RoomsCount({ handleChange, defaultValue, multiRoom, formIndex, validation, warningMessage, halfPayRule }) {
    //general variables
    const { control } = useForm();
    const { locale } = useContext(LocaleContext);
    const [inputValue, setInputValue] = useState("");

    const PERSON_TYPES = [
        { label: t("adult"), value: "adult", disable: 1 },
        { label: t("child"), value: "child", disable: 0 },
    ];

    const createAgeList = maxAge => {
        return Array.from({ length: maxAge }, (_, index) => {
            const age = index + 1;
            return {
                id: age,
                name: `${age} ${t("General.Years Old")}`,
                english_name: `years old ${age}`,
            };
        });
    };
    const AGES_LIST = createAgeList(11);

    // Initialize a room with default values
    const initializeRoom = () => ({
        adult: 1,
        child: 0,
        childAges: [],
    });

    //Popover
    const [anchorEl, setAnchorEl] = useState(null);
    const handleClick = event => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    //room counter
    const [roomsCount, setRoomsCount] = useState([initializeRoom()]);

    // Handle changes in the room counter (increment or decrement)
    const handleCounterChange = (event, actionType, roomIndex) => {
        event.preventDefault();
        const { name, value } = event.target;
        const currentRoom = roomsCount[roomIndex];
        let updatedRooms;

        if (actionType === "increment") {
            const isValidIncrement = validateIncrement(currentRoom?.[name], validation);
            if (isValidIncrement) {
                updatedRooms = updateRoomCount(roomsCount, roomIndex, name, parseInt(value) + 1);
            } else {
                toast.warning(warningMessage, { toastId: "custom-id-yes" });
                return;
            }
        }

        if (actionType === "decrement") {
            updatedRooms = updateRoomCount(roomsCount, roomIndex, name, parseInt(value) - 1);
            if (name === "child" && parseInt(value) === updatedRooms[roomIndex]?.childAges?.length) {
                updatedRooms[roomIndex].childAges.pop();
            }
        }

        setRoomsCount(updatedRooms);
    };

    // Validation function to check if the current value can be incremented
    const validateIncrement = (currentValue, maxValue) => currentValue < maxValue;

    // update the room count for a specific room
    const updateRoomCount = (rooms, roomIndex, name, newValue) => {
        const updatedRooms = [...rooms];
        updatedRooms[roomIndex][name] = newValue;
        return updatedRooms;
    };

    //handle child age change
    const handleAgeChange = (newAge, _, roomIndex, ageIndex) => {
        const updatedRooms = [...roomsCount];
        updatedRooms[roomIndex].childAges[ageIndex] = newAge;
        setRoomsCount(updatedRooms);
    };

    //calculate total number of passengers (adults + children) across all rooms
    const totalPassengers = roomsCount?.reduce((total, room) => total + room?.adult + room?.child, 0);

    //add room
    const addRoom = e => {
        e.preventDefault();
        setRoomsCount(values => [...values, initializeRoom()]);
    };

    //remove room
    const removeRoom = index => {
        let updatedRooms = [...roomsCount];
        updatedRooms.splice(index, 1);
        setRoomsCount(updatedRooms);
    };

    // Validation function to check room details
    const validateRoom = (room, maxInfantAge, maxPassengers) => {
        if (room.child !== room.childAges.length) {
            toast.warning(t("Toast Messages.Child Age Input"));
            return false;
        }

        const validChildren = room.childAges.filter(age => age.id >= maxInfantAge);
        if (validChildren.length + room.adult > maxPassengers) {
            toast.warning(warningMessage, { toastId: "custom-id-yes" });
            return false;
        }

        return true;
    };

    // submit the room count after validation
    const submitRoomCount = () => {
        const validateRooms = roomsCount.map(room => {
            return validateRoom(room, halfPayRule?.conditions?.max_infant_age, validation);
        });
        if (validateRooms.includes(false)) {
            return;
        } else {
            handleClose();
            handleChange(roomsCount, totalPassengers, formIndex);
        }
    };

    useEffect(() => {
        handleChange(roomsCount, totalPassengers, formIndex);
    }, [roomsCount]);

    //default value
    useEffect(() => {
        if (!!defaultValue) {
            setRoomsCount(defaultValue);
        }
    }, []);

    return (
        <Grid className="rooms-count" container item xs={12}>
            <BaseTextField
                dir={locale === "en" ? "ltr" : "rtl"}
                aria-describedby={id}
                onClick={handleClick}
                label={t("roomCountTitle")}
                value={`${roomsCount?.length} ${t("room")} ، ${totalPassengers} ${t("passenger")}`}
                radius="10px"
                readOnly={true}
            />
            <BasePopover id={id} open={open} anchorEl={anchorEl} handleClose={handleClose} width="18%" vertical="bottom" horizontal={locale === "en" ? "left" : "right"}>
                <Grid component="form" container item xs={12} rowGap={1} justifyContent="space-between">
                    {roomsCount?.map((room, roomIndex) => {
                        return (
                            <Grid key={roomIndex} container item xs={12} rowGap={1}>
                                <Grid container item xs="auto" columnGap={1} alignItems="center">
                                    {multiRoom && (
                                        <span className="rooms-count__room-title">
                                            {t("room")} {roomIndex + 1}
                                        </span>
                                    )}
                                    {roomsCount.length > 1 && (
                                        <Trash
                                            className="rooms-count__remove-room"
                                            size="18"
                                            color="var(--red-color)"
                                            onClick={() => removeRoom(roomIndex)}
                                        />
                                    )}
                                </Grid>
                                {PERSON_TYPES.map(person => (
                                    <Grid container item xs={12} justifyContent="space-between" alignItems="center" key={person.value}>
                                        <span className="rooms-count__age-range">{person.label}</span>
                                        <Grid container item xs={4} justifyContent="space-between" alignItems="center">
                                            <Grid container item xs={3} justifyContent="center" order={locale === "en" ? 3 : 1}>
                                                <button
                                                    className="rooms-count__button"
                                                    name={person?.value}
                                                    value={roomsCount[roomIndex]?.[person?.value]}
                                                    onClick={e => handleCounterChange(e, "increment", roomIndex)}
                                                >
                                                    +
                                                </button>
                                            </Grid>
                                            <Grid container item xs={6} justifyContent="center" order={2}>
                                                <span className="rooms-count__counter">{room?.[person?.value]}</span>
                                            </Grid>
                                            <Grid container item xs={3} justifyContent="center" order={locale === "en" ? 1 : 3}>
                                                <button
                                                    className="rooms-count__button"
                                                    name={person?.value}
                                                    value={roomsCount[roomIndex]?.[person?.value]}
                                                    onClick={e => handleCounterChange(e, "decrement", roomIndex)}
                                                    disabled={roomsCount[roomIndex]?.[person?.value] === person?.disable}
                                                >
                                                    -
                                                </button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                ))}

                                <Grid container item xs={12} rowGap={1} justifyContent="space-between">
                                    {[...Array(roomsCount[roomIndex]?.child)]?.map((_, ageIndex) => {
                                        return (
                                            <Grid container item xs={5.9}>
                                                <AutoCompleteComponent
                                                    label={t("ChildAge")}
                                                    index={roomIndex}
                                                    nestIndex={ageIndex}
                                                    options={AGES_LIST}
                                                    name={`ages${roomIndex}${ageIndex}`}
                                                    defaultValue={roomsCount?.[roomIndex]?.childAges?.[ageIndex] || null}
                                                    handleChange={handleAgeChange}
                                                    setInputValue={setInputValue}
                                                    control={control}
                                                    width={350}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Grid>
                        );
                    })}

                    <Grid className="rooms-count__add-room" container item xs={5.8} md={8} lg={6}>
                        {multiRoom && (
                            <BaseButton
                                text={t("addRoom")}
                                color="outline"
                                rightIcon={<Add size="16" color="var(--primary-color)" />}
                                size="small"
                                radius="radius-10"
                                handleClick={addRoom}
                                disabled={roomsCount?.length === 4}
                            />
                        )}
                    </Grid>
                    <Grid container item xs={5.8}>
                        <BaseButton
                            text={t("confirmation")}
                            color="primary"
                            size="small"
                            radius="radius-10"
                            type="button"
                            handleClick={submitRoomCount}
                        />
                    </Grid>
                </Grid>
            </BasePopover>
        </Grid>
    );
}

export default RoomsCount;
